import React, { useCallback, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { Message } from "semantic-ui-react";

import DENRoot, { DENContext } from "./DENRoot";
import { DENHostProps } from "./DENInterface";
import { HistoryStateInitial } from "./DPOI";
import Diagram from "./diagram/Diagram";
import HistoryReviewTab from "./HistoryReviewTab";
import ModalGallery from "./ModalGallery";
import { checkIsEditDiagram, getDENRootKey } from "./DENUtils";

type CardDentalHistoryProps = DENHostProps & { scrollable?: boolean };

// Dental History
// โหลดประวัติเมื่อกดค้นหา
const CardDentalHistory = (props: CardDentalHistoryProps) => (
  <DENRoot key={getDENRootKey(props)} {...props} onLoaded={handleLoaded}>
    {(den: DENContext) => <DentalHistory den={den} />}
  </DENRoot>
);

const handleLoaded = (den: DENContext) => {
  if (den.state.encounterId) {
    den.onEvent({ message: "GetSuperToothList", params: { encounter: den.state.encounterId } });
  }
};

// sub tab ของ History Review → ชุดข้อมูลของ Diagram (0 Visit / 1 Examination / 2 Treatment plan)
const HISTORY_KEYS = [
  { prefix: "historyVisit", periodontium: "historyVisitPeriodontiumRecord" }, // 0 Visit
  { prefix: "history", periodontium: "historyPeriodontiumRecord" }, // 1 Examination
  { prefix: "historyTreatment", periodontium: "historyTreatmentPeriodontiumRecord" }, // 2 Treatment plan
];

const DentalHistory = ({ den }: { den: DENContext }) => {
  const { state, controller, onEvent, setProp, setState } = den;
  const intl = useIntl();

  const stateRef = useRef(state);

  stateRef.current = state;

  const keys = HISTORY_KEYS[state.historyReviewSubTab] || HISTORY_KEYS[0];
  const clinicalFindings = state[`${keys.prefix}ClinicalFindingList`];
  const clinicalFindingId = state[`${keys.prefix}ClinicalFindingId`];

  const isEdit = useMemo(() => {
    const list =
      state.historyReviewSubTab === 1 && clinicalFindingId
        ? (clinicalFindings || []).filter((item: any) => item.id === clinicalFindingId)
        : clinicalFindings;

    return checkIsEditDiagram(state, controller.data, {
      clinicalFindingList: list || [],
      periodontiumData: state[keys.periodontium]?.data || {},
    });
  }, [
    state.historyReviewSubTab,
    clinicalFindings,
    clinicalFindingId,
    state[keys.periodontium],
    state.dentalExamData,
    state.faceExamData,
    state.muscleExamData,
    state.softTissueExamData,
    state.periodontalExamData,
    controller.data?.organ,
  ]);

  /* ------------------------------------------------------ */

  /*                         Handler                         */

  /* ------------------------------------------------------ */
  const handleNoAction = useCallback(() => {}, []);

  const handleGetHistoryVisit = useCallback(async () => {
    if (stateRef.current.isDataSecret) {
      return;
    }

    return onEvent({ message: "handleGetHistoryVisit", params: {} });
  }, []);

  const handleGetHistoryEx = useCallback(async () => onEvent({ message: "handleGetHistoryEx", params: {} }), []);

  const handleGetHistoryTp = useCallback(async () => onEvent({ message: "handleGetHistoryTp", params: {} }), []);

  const handleCloseTreatmentRecord = useCallback(() => {
    setState({ selectedHistoryTp: HistoryStateInitial.selectedHistoryTp, hideTreatmentForm: true });
  }, []);

  const handleOnFocusFindingsRowEx = useCallback((_state: any, rowInfo: any) => {
    const current = stateRef.current;

    return {
      onClick: () => {
        const list = stateRef.current.historyClinicalFindingList || [];
        const index = list.findIndex((item: any) => item.id === rowInfo.original.id);

        if (rowInfo.original?.encounter) {
          onEvent({ message: "GetPeriodontium", params: { history: true, encounterId: rowInfo.original?.encounter } });
        }

        setState({
          selectedHistoryEx: rowInfo.original,
          historyClinicalFindingIndex: index === -1 ? null : index,
          historyClinicalFindingId: list[index]?.id,
        });
      },
      className: rowInfo?.original && rowInfo?.original?.id === current.selectedHistoryEx?.id ? "selected" : "",
      style: { cursor: "pointer" },
    };
  }, [state.selectedHistoryEx]);

  const handleOnFocusFindingsRowVisit = useCallback((_state: any, rowInfo: any) => {
    const current = stateRef.current;

    return {
      onClick: () => {
        if (rowInfo.original?.encounter) {
          onEvent({ message: "GetExamination", params: { encounter: rowInfo.original?.encounter, visit: true } });
          onEvent({ message: "GetPeriodontium", params: { visit: true, encounterId: rowInfo.original?.encounter } });
        }

        if (rowInfo.original?.patient_file) {
          setState({
            isMainLoading: false,
            openModMessage: true,
            messageTitle: "ข้อมูลการรักษา",
            errorMessage: null,
            successMessage: rowInfo.original?.patient_file,
          });
        }

        setState({ selectedHistoryVisit: rowInfo.original });
      },
      className: rowInfo?.original && rowInfo?.original?.id === current.selectedHistoryVisit?.id ? "selected" : "",
      style: { cursor: "pointer" },
    };
  }, [state.selectedHistoryVisit]);

  const handleOnFocusFindingsRowTp = useCallback((_state: any, rowInfo: any) => {
    const current = stateRef.current;

    return {
      onClick: () => {
        if (rowInfo.original?.treatment_issue?.encounter) {
          onEvent({ message: "GetExamination", params: { encounter: rowInfo.original?.treatment_issue?.encounter, treatment: true } });
          onEvent({ message: "GetPeriodontium", params: { treatment: true, encounterId: rowInfo.original?.encounter } });
        }

        setState({ selectedHistoryTp: rowInfo.original });
      },
      className: rowInfo?.original && rowInfo?.original?.id === current.selectedHistoryTp?.id ? "selected" : "",
      style: { cursor: "pointer" },
    };
  }, [state.selectedHistoryTp]);

  const handleCloseGallery = useCallback(() => {
    onEvent({ message: "handleOpenModGallery", params: { data: null } });
  }, []);

  const encounter = state.encounterList?.[state.encounterIndex as any] || null;

  // ผู้ป่วยปกปิด ไม่แสดงประวัติ
  if (state.isDataSecret) {
    return <Message warning size="small" content="ไม่สามารถแสดงประวัติการรักษาได้ เนื่องจากข้อมูลผู้ป่วยถูกปกปิด" />;
  }

  return (
    <>
      <div className="den-diagram">
        <Diagram
          isEditDental={isEdit.isEditDental}
          isEditMuscle={isEdit.isEditMuscle}
          isEditFace={isEdit.isEditFace}
          isEditPlaque={isEdit.isEditPlaque}
          isEditSoft={isEdit.isEditSoft}
          isEditJaw={isEdit.isEditJaw}
          activeIndexDiagram={state.activeIndexDiagram}
          selectorMode={state.selectorMode}
          hiddenEvaluation={true}
          clinicalFindings={clinicalFindings}
          clinicalFindingId={clinicalFindingId}
          clinicalFindingIndex={state[`${keys.prefix}ClinicalFindingIndex`]}
          filterClinicalFindingIds={state[`${keys.prefix}FilterClinicalFindingIds`]}
          filterItem={state.filterItem}
          setHideBabyTooth={handleNoAction}
          hideBabyTooth={
            typeof state.patientDetail?.hideBabyTooth === "boolean"
              ? state.patientDetail.hideBabyTooth
              : !state.userPermisstion?.role_PEDIATRIC_DENTISTS
          }
          setSuperTooth={handleNoAction}
          superteeth={(state.supernumeraryteeth || []).map((item: any) => item.nickName)}
          setMissingTooth={handleNoAction}
          controller={controller}
          onEvent={onEvent}
          setProp={setProp}
          masterData={controller.data}
          sub="DiagnosisHistory"
          historyReviewSubTab={state.historyReviewSubTab}
          // ----- Plaque Record
          patientDetail={state.patientDetail}
          encounterInfo={state.encounterInfo}
          missingteeth={state.patientDetail?.missingteeth || []}
          partialmissingteeth={state.patientDetail?.partialmissingteeth}
          implantteeth={state.patientDetail?.implantteeth || []}
          periodontiumHistoryList={state.periodontiumHistoryList}
          periodontalExamData={state.periodontalExamData}
          periodontiumRecord={state.periodontiumRecord}
          historyPeriodontiumRecord={state.historyPeriodontiumRecord}
          historyVisitPeriodontiumRecord={state.historyVisitPeriodontiumRecord}
          historyTreatmentPeriodontiumRecord={state.historyTreatmentPeriodontiumRecord}
          // ----- End Plaque Record
          encounter={encounter}
          encounterIndex={state.encounterIndex}
          showDrawingChecked={state.showDrawingChecked}
          dentalCariesRisk={state.dentalCariesRisk}
          activeTabDentalCaries={state.activeTabDentalCaries}
          openModalAssessmentResults={state.openModalAssessmentResults}
          openModAssessment={state.openModAssessment}
          assessmentResults={state.assessmentResults}
          // Exam
          muscleExamData={state.muscleExamData}
          softTissueExamData={state.softTissueExamData}
          faceExamData={state.faceExamData}
          dentalExamData={state.dentalExamData}
          activeEvaluation={state.activeEvaluation}
        />
      </div>

      <div style={{ width: "100%" }} className={`history-tab active-${state.activeIndexDiagram}`}>
        <HistoryReviewTab
          onEvent={onEvent}
          setProp={setProp}
          // ---------- Start Review visit data
          patientFamilyTPList={state.patientFamilyTPList}
          historyReviewVisitData={state.historyReviewVisitData}
          patientId={state.patientId}
          languageUX={intl.locale}
          historyDate={state.historyDate}
          historyReviewSubTab={state.historyReviewSubTab}
          // filter
          doctorFilterList={state.doctorFilterList}
          clinicFilterList={state.clinicFilterList}
          openModalFilter={state.openModalFilter}
          openModalFilterClinic={state.openModalFilterClinic}
          // callback
          handleGetHistoryVisit={handleGetHistoryVisit}
          handleOnFocusFindingsRowVisit={handleOnFocusFindingsRowVisit}
          // ---------- End Review visit data
          // ---------- Start Review ex data
          historyReviewExData={state.historyReviewExData}
          organItems={controller.data?.organ?.items}
          showDrawingChecked={state.showDrawingChecked}
          // filter
          doctorFilterListEx={state.doctorFilterListEx}
          clinicFilterListEx={state.clinicFilterListEx}
          locationFilterList={state.locationFilterList}
          clinicalFindingFilterList={state.clinicalFindingFilterList}
          openModalDoctorExFilter={state.openModalDoctorExFilter}
          openModalFilterClinicEx={state.openModalFilterClinicEx}
          openModalFilterLocation={state.openModalFilterLocation}
          openModalFilterClinicalFinding={state.openModalFilterClinicalFinding}
          // callback
          handleGetHistoryEx={handleGetHistoryEx}
          handleOnFocusFindingsRowEx={handleOnFocusFindingsRowEx}
          // ---------- End Review ex data
          // ---------- Start Review tp data
          historyReviewTpData={state.historyReviewTpData}
          treatmentRecord={state.treatmentRecord}
          hideTreatmentForm={state.hideTreatmentForm}
          selectedHistoryTp={state.selectedHistoryTp}
          // filter
          doctorFilterListTp={state.doctorFilterListTp}
          clinicFilterListTp={state.clinicFilterListTp}
          locationFilterListTp={state.locationFilterListTp}
          treatmentFilterListTp={state.treatmentFilterListTp}
          treatmentCodeFilterListTp={state.treatmentCodeFilterListTp}
          statusFilterList={state.statusFilterList}
          openModalDoctorTpFilter={state.openModalDoctorTpFilter}
          openModalFilterClinicTp={state.openModalFilterClinicTp}
          openModalFilterLocationTp={state.openModalFilterLocationTp}
          openModalFilterTreatmentTp={state.openModalFilterTreatmentTp}
          openModalFilterTreatmentCodeTp={state.openModalFilterTreatmentCodeTp}
          openModalFilterStatus={state.openModalFilterStatus}
          // callback
          handleGetHistoryTp={handleGetHistoryTp}
          handleOnFocusFindingsRowTp={handleOnFocusFindingsRowTp}
          handleCloseTreatmentRecord={handleCloseTreatmentRecord}
          // SubTreatmentRecord Issue 67238
          anestheticOptions={state.anestheticOptions}
          // ---------- End Review tp data
        />

        <ModalGallery
          open={!!state.openModGallery}
          onClose={handleCloseGallery}
          items={state.openModGallery?.pictures || []}
          visitDate={state.openModGallery?.started || ""}
          doctorName={state.openModGallery?.division_name || ""}
          divisionName={state.openModGallery?.doctor_fullname || ""}
        />
      </div>
    </>
  );
};

export default CardDentalHistory;
